import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= TYPES ================= */

export type CartExtra = {
  id: string;
  name: string;
 rate: number;
  qty?: number;
};

export type CartItem = {
  listingId: string;
  brandId: string;   
  hostId: string;
  name: string;
  rate: number;
  photo?: string;
  qty: number;
  colorId?: number;
  sizeId?: number;
};

export type AddToCartPayload = {
  listingId: string;
  brandId: string;   
  hostId: string;
  name: string;
  rate: number;
  photo?: string;
  qty?: number;
  colorId?: number;
  sizeId?: number;
};

export type VariantKey = {
  listingId: string;
  colorId?: number;
  sizeId?: number;
  variantId?: string;
};

export type CartState = {
  userId: string | null;
  items: CartItem[];
  extras: CartExtra[];
};

/* ================= STATE ================= */

const initialState: CartState = {
  userId: null,
  items: [],
  extras: [],
};

/* ================= SLICE ================= */

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartUser: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },

    /* ===== ADD TO CART ===== */
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const qtyToAdd = action.payload.qty ?? 1;

      // Fixed: explicitly handles matching optional properties
      const existing = state.items.find(
        (i) =>
          i.listingId === action.payload.listingId &&
          i.colorId === action.payload.colorId &&
          i.sizeId === action.payload.sizeId
      );

      if (existing) {
        existing.qty += qtyToAdd;
      } else {
        state.items.push({
          listingId: action.payload.listingId,
          brandId: action.payload.brandId,
          hostId: action.payload.hostId,
          name: action.payload.name,
          rate: action.payload.rate,
          photo: action.payload.photo,
          qty: qtyToAdd,
          colorId: action.payload.colorId,
          sizeId: action.payload.sizeId,
        });
      }
    },

    /* ===== ADD EXTRA ===== */
    addExtra: (state, action: PayloadAction<CartExtra>) => {
      const existing = state.extras.find((e) => e.id === action.payload.id);
      if (existing) {
        existing.qty = (existing.qty || 1) + (action.payload.qty || 1);
      } else {
        state.extras.push({
          ...action.payload,
          qty: action.payload.qty || 1,
        });
      }
    },

    /* ===== REMOVE EXTRA ===== */
    removeExtra: (state, action: PayloadAction<string>) => {
      state.extras = state.extras.filter((e) => e.id !== action.payload);
    },

    /* ===== DECREMENT ===== */
    decrementCartItem: (state, action: PayloadAction<VariantKey>) => {
      const item = state.items.find(
        (i) =>
          i.listingId === action.payload.listingId &&
          i.colorId === action.payload.colorId &&
          i.sizeId === action.payload.sizeId
      );

      if (!item) return;

      if (item.qty > 1) {
        item.qty -= 1;
      } else {
        state.items = state.items.filter(
          (i) =>
            !(
              i.listingId === action.payload.listingId &&
              i.colorId === action.payload.colorId &&
              i.sizeId === action.payload.sizeId
            )
        );
      }
    },

    /* ===== REMOVE ===== */
    removeFromCart: (state, action: PayloadAction<VariantKey>) => {
      state.items = state.items.filter(
        (i) =>
          !(
            i.listingId === action.payload.listingId &&
            i.colorId === action.payload.colorId &&
            i.sizeId === action.payload.sizeId
          )
      );
    },

    clearCart: (state) => {
      state.items = [];
      state.extras = [];
    },
  },
});

export const {
  setCartUser,
  addToCart,
  addExtra,
  removeExtra,
  decrementCartItem,
  removeFromCart,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;

